import { computed, inject, untracked } from '@angular/core';
import { patchState, signalStore, withComputed, withFeature, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';

import { AssetName } from '@core/api/assets/assets.models';
import { AssetsService } from '@core/api/assets/assets.service';

import { withApiResource } from '@shared/stores/features/with-api-resource.feature';
import { withCancellation } from '@shared/stores/features/with-cancellation.feature';

interface ChargerNamesState {
  siteId: string | undefined;
  chargerNames: AssetName[] | undefined;
}

const initialState: ChargerNamesState = {
  siteId: undefined,
  chargerNames: undefined,
};

export const ChargerNamesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCancellation('clear'),
  withFeature((store) => {
    const assetsService = inject(AssetsService);
    return withApiResource('chargerNames', (siteId: string) => assetsService.getChargerNames$(siteId), store.clear$);
  }),
  withComputed(({ chargerNames, chargerNamesIsLoading, chargerNamesError }) => ({
    chargerNamesById: computed(() => Object.fromEntries((chargerNames() ?? []).map((charger) => [charger.id, charger.name]))),
    isLoading: computed(() => chargerNamesIsLoading()),
    error: computed(() => (chargerNamesError() ? 'Failed to load charger names' : undefined)),
  })),
  withMethods((store) => {
    const applyLoadSuccess = rxMethod<{ assets: AssetName[] }>(pipe(tap(({ assets }) => patchState(store, { chargerNames: assets }))));
    applyLoadSuccess(store.chargerNamesLoadSuccess$);

    return {
      ensureChargerNamesLoaded(siteId: string): void {
        const siteIsLoadedOrLoading = untracked(
          () => store.siteId() === siteId && (store.chargerNames() !== undefined || store.isLoading())
        );
        if (siteIsLoadedOrLoading) {
          return;
        }
        patchState(store, { siteId, chargerNames: undefined });
        store.chargerNamesLoad(siteId);
      },

      clearChargerNames(): void {
        store.clear();
        patchState(store, initialState);
      },

      chargerNamesForSite(siteId: string): AssetName[] | undefined {
        return store.siteId() === siteId ? store.chargerNames() : undefined;
      },

      chargerNameForSite(siteId: string, chargerId: string): string | undefined {
        return store.siteId() === siteId ? store.chargerNamesById()[chargerId] : undefined;
      },

      chargerNamesHaveLoadedForSite(siteId: string): boolean {
        return store.siteId() === siteId && store.chargerNames() !== undefined;
      },

      chargerExistsForSite(siteId: string, chargerId: string): boolean {
        return store.siteId() === siteId && store.chargerNames()?.some((charger) => charger.id === chargerId) === true;
      },

      chargerNamesAreLoadingForSite(siteId: string): boolean {
        return store.siteId() !== siteId || store.isLoading();
      },

      chargerNamesErrorForSite(siteId: string): string | undefined {
        return store.siteId() === siteId ? store.error() : undefined;
      },
    };
  })
);

export type ChargerNamesStoreInstance = InstanceType<typeof ChargerNamesStore>;
